/**
 * The ONE place the runtime client touches `@rogueworlds/voxel-bridge`.
 *
 * Everything the client needs from the isomorphic voxel engine is re-exported here behind explicit,
 * documented wrappers, so a signature change in the package is a one-file fix instead of a hunt
 * through `world/`, `net/` and `main.ts`.
 *
 * Verified surface (packages/voxel-bridge): `generateChunk(cx, cy, cz, seed): Chunk`,
 * `surfaceHeightAt(bx, bz, seed): number` (integer voxel Y in [40, 120]), `sampleBlock(bx, by, bz, seed)`,
 * `chunkIndex(lx, ly, lz)`, `AIR`, `BLOCK_IDS`/`BLOCKS`/`isSolid`, and
 * `class Chunk { cx, cy, cz, size, blocks, palette, rev; get; set; serialize(); toSnapshot();
 * static deserialize(snapshot) }`.
 *
 * `generateChunkChecked` performs a one-time self-check of the argument order: an order swap still
 * type-checks (`number, number, number, number`), so the first chunk's own coordinates are compared
 * against the coordinates that were requested and a loud error is logged instead of rendering a
 * scrambled world.
 */

import { CHUNK_SIZE } from "@rogueworlds/shared";
import {
  AIR,
  BLOCK_IDS,
  BLOCKS,
  Chunk,
  generateChunk,
  isSolid,
  sampleBlock,
  surfaceHeightAt,
} from "@rogueworlds/voxel-bridge";
import type { BlockDef, MeshData, VoxelEditAbs } from "@rogueworlds/voxel-bridge";

export {
  AIR,
  BASALT,
  BLOCK_IDS,
  BLOCKS,
  Chunk,
  DIRT,
  GLASS,
  GRASS,
  LEAVES,
  LOG,
  PLANK,
  SAND,
  SNOW,
  STONE,
  WATER,
  chunkIndex,
  generateChunk,
  isSolid,
  sampleBlock,
  surfaceHeightAt,
} from "@rogueworlds/voxel-bridge";
export type { BlockDef, MeshData, VoxelEditAbs } from "@rogueworlds/voxel-bridge";

/** Chunk edge length in meters (32 voxels * 0.1 m). */
export const CHUNK_EDGE_M = CHUNK_SIZE * 0.1;

let generateVerified = false;

/**
 * `generateChunk` plus a one-time argument-order self-check, so a silently swapped
 * `generateChunk(seed, cx, cy, cz)` cannot render a scrambled world.
 */
export function generateChunkChecked(seed: number, cx: number, cy: number, cz: number): Chunk {
  const chunk = generateChunk(cx, cy, cz, seed);
  if (!generateVerified) {
    generateVerified = true;
    if (chunk.cx !== cx || chunk.cy !== cy || chunk.cz !== cz) {
      console.error(
        "[voxel-bridge] generateChunk argument order mismatch: asked for " +
          `(${cx}, ${cy}, ${cz}) and got (${chunk.cx}, ${chunk.cy}, ${chunk.cz}). ` +
          "Expected generateChunk(cx, cy, cz, seed).",
      );
    }
  }
  return chunk;
}

let heightChecked = false;

/** `surfaceHeightAt` with a one-time sanity check on the returned voxel height. */
export function surfaceHeightAtChecked(seed: number, vx: number, vz: number): number {
  const height = surfaceHeightAt(vx, vz, seed);
  if (!heightChecked) {
    heightChecked = true;
    if (!Number.isFinite(height) || height < 0 || height > 4096) {
      console.error(`[voxel-bridge] surfaceHeightAt returned ${height} at (${vx}, ${vz}); expected a voxel Y.`);
    }
  }
  return height;
}

/** World voxel coordinate -> chunk coordinate (floor division, negatives included). */
export function chunkOfVoxel(v: number): number {
  return Math.floor(v / CHUNK_SIZE);
}

/** World voxel coordinate -> 0..31 chunk-local coordinate. */
export function localOfVoxel(v: number, chunkCoord: number): number {
  return v - chunkCoord * CHUNK_SIZE;
}

/**
 * Chunk-local voxel key, `x | z << 5 | y << 10` (0..32767). Identical to the bridge's `chunkIndex`
 * (x fastest, then z, then y), but packed here so no code in the runtime depends on the bridge's
 * index formula; it is used only for the sparse edit overlay.
 */
export function packVoxel(x: number, y: number, z: number): number {
  return (x & 31) | ((z & 31) << 5) | ((y & 31) << 10);
}

export function unpackVoxelX(packed: number): number {
  return packed & 31;
}

export function unpackVoxelZ(packed: number): number {
  return (packed >> 5) & 31;
}

export function unpackVoxelY(packed: number): number {
  return (packed >> 10) & 31;
}
